// src/screens/mypage/WriteInquiryScreen.js
// 72차 신규: 문의 작성 화면 — 두 창구를 하나의 화면으로 공용 처리한다.
//   targetType:'app'   — 마이탭 "문의 내역"에서 진입, storeId/context 없음
//   targetType:'store' — 예약 내역/테마 상세 화면에서 진입, storeId + context(예약/테마) prefilled
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체(화면 제목은 본문 상단 텍스트로 유지)
// 78차: 제출 버튼(인라인 골드 fill)을 공용 GradientButton(Primary)으로 교체
// 테마수정/테마추가/테마삭제 카테고리 신규(app 전용, 매장관리자만 노출) — 총괄관리자만
// Firestore의 테마 배열을 직접 수정할 수 있어서, 매장관리자가 이 세 카테고리 중 하나를
// 고르면 자유입력 대신 테마명/설명/난이도/장르/플레이타임(전부 필수)+인원별 가격(선택)+
// 테마 이미지(필수) 구조화 입력 폼으로 전환하고, 제출 시 "[카테고리] 매장명" 제목과
// 정리된 본문을 자동으로 만들어준다. 매장을 여러 개 관리 중이면 어느 매장 건인지 먼저
// 고르게 함(그동안은 managedStores[0]으로만 자동 지정돼 다른 매장 요청을 못 보냈음).
import React, { useState, useEffect } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, Image, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDoc, doc } from 'firebase/firestore';
import { storage, db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createInquiry } from '../../services/inquiryService';
import { COLORS } from '../../constants/theme';
import { INQUIRY_CATEGORIES, THEME_REQUEST_CATEGORIES, GENRES, DIFFICULTY_META } from '../../constants';
import AppHeader, { LogoMark } from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { MenuReservationIcon, IconLabel } from '../../components/common/Icons';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;

const CONTEXT_LABEL = {
  reservation: { label: '이 예약에 대한 문의', Icon: MenuReservationIcon },
  theme:       { label: '이 테마에 대한 문의', Icon: LogoMark },
};

const DIFFICULTY_OPTIONS = Object.entries(DIFFICULTY_META).map(([value, meta]) => ({ value, label: meta.label }));

export default function WriteInquiryScreen({ navigation, route }) {
  const insets = useSafeAreaInsets();
  const { user, profile } = useAuth();
  const { targetType = 'app', storeId: routeStoreId = null, context = null } = route?.params || {};
  // 75차: 매장관리자가 총괄관리자에게 보내는 문의(정산 이의제기 등)를 일반 회원 문의와
  // 구분해서 총관리자 화면에 보이도록 스냅샷. targetType:'app'인데 storeId가 안 넘어온
  // 경우(마이탭 "문의 내역"에서 바로 작성) 매장관리자면 자기 매장을 자동으로 붙여준다 —
  // 총괄관리자가 "어느 매장 건인지" 알아야 정산 이의제기를 처리할 수 있음.
  const senderRole = profile?.adminRole === 'store' ? 'store' : 'user';
  const managedStoreIds = Array.isArray(profile?.managedStores) ? profile.managedStores : (profile?.managedStores ? [profile.managedStores] : []);
  const defaultStoreId = routeStoreId || (senderRole === 'store' ? (managedStoreIds[0] || null) : null);

  const [title,    setTitle]    = useState('');
  const [content,  setContent]  = useState('');
  const [saving,   setSaving]   = useState(false);
  // 78차 35차: 카테고리 선택 — 지금까지 제목/내용 자유입력뿐이라 총괄관리자가 목록에서
  // 문의 성격을 바로 알 수 없었음. targetType별로 다른 목록(app/store) 사용.
  const [category, setCategory] = useState(null);
  const rawCategories = INQUIRY_CATEGORIES[targetType] || INQUIRY_CATEGORIES.app;
  // 테마수정/추가/삭제는 매장관리자만 의미 있는 요청이라 일반 회원에겐 숨김
  const categories = senderRole === 'store'
    ? rawCategories
    : rawCategories.filter(c => !THEME_REQUEST_CATEGORIES.includes(c.value));

  const isThemeRequest = THEME_REQUEST_CATEGORIES.includes(category);

  // 매장을 여러 개 관리하는 경우 어느 매장 건인지 선택 — 테마 요청 카테고리에서만 필요.
  const [storeOptions,   setStoreOptions]   = useState([]); // [{id, ownerName}]
  const [selectedStoreId, setSelectedStoreId] = useState(defaultStoreId);
  const needsStorePicker = isThemeRequest && managedStoreIds.length > 1;

  useEffect(() => {
    if (!isThemeRequest || managedStoreIds.length <= 1 || storeOptions.length) return;
    (async () => {
      const entries = await Promise.all(managedStoreIds.map(async (sid) => {
        try {
          const snap = await getDoc(doc(db, 'stores', sid));
          return snap.exists() ? { id: sid, ownerName: snap.data().ownerName || sid } : null;
        } catch { return null; }
      }));
      setStoreOptions(entries.filter(Boolean));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThemeRequest, managedStoreIds.join(',')]);

  // 테마 요청 전용 구조화 입력 필드
  const [themeName,       setThemeName]       = useState('');
  const [themeDesc,        setThemeDesc]       = useState('');
  const [themeDifficulty,  setThemeDifficulty] = useState(null);
  const [themeGenre,       setThemeGenre]      = useState(null);
  const [themePlaytime,    setThemePlaytime]   = useState('');
  const [themeBreakTime,   setThemeBreakTime]  = useState('');
  const [themePricing,     setThemePricing]    = useState('');
  const [themeImageUri,    setThemeImageUri]   = useState(null);
  const [uploadingImage,   setUploadingImage]  = useState(false);

  const handlePickThemeImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      showAlert('권한 필요', '사진 접근 권한이 필요해요. 설정에서 허용해주세요.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsMultipleSelection: false,
      quality: 0.7,
      aspect: [4, 3],
    });
    if (!result.canceled && result.assets?.[0]) {
      setThemeImageUri(result.assets[0].uri);
    }
  };

  const uploadThemeImage = async (uri) => {
    const manipulated = await ImageManipulator.manipulateAsync(
      uri, [{ resize: { width: 1200 } }], { compress: 0.75, format: ImageManipulator.SaveFormat.JPEG }
    );
    const res  = await fetch(manipulated.uri);
    const blob = await res.blob();
    const filename   = `inquiries/${user.uid}_${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
    const storageRef = ref(storage, filename);
    await uploadBytes(storageRef, blob);
    return getDownloadURL(storageRef);
  };

  const findStoreName = () => {
    if (storeOptions.length) return storeOptions.find(s => s.id === selectedStoreId)?.ownerName || '';
    return profile?.nickname || '';
  };

  const handleSubmit = async () => {
    if (!category) {
      showAlert('입력 필요', '문의 카테고리를 선택해주세요.');
      return;
    }

    if (isThemeRequest) {
      const effectiveStoreId = needsStorePicker ? selectedStoreId : defaultStoreId;
      if (!effectiveStoreId) {
        showAlert('입력 필요', '어느 매장에 대한 요청인지 선택해주세요.');
        return;
      }
      if (!themeName.trim() || !themeDesc.trim() || !themeDifficulty || !themeGenre || !themePlaytime.trim() || !themeBreakTime.trim()) {
        showAlert('입력 필요', '테마명·설명·난이도·장르·플레이타임·휴게시간은 모두 입력해주세요.');
        return;
      }
      if (!themeImageUri) {
        showAlert('입력 필요', '테마 이미지를 첨부해주세요.');
        return;
      }
      setSaving(true);
      setUploadingImage(true);
      try {
        const imageUrl = await uploadThemeImage(themeImageUri);
        setUploadingImage(false);
        const categoryLabel = categories.find(c => c.value === category)?.label || '';
        const storeName = findStoreName();
        const finalTitle = `[${categoryLabel}] ${storeName}`;
        const finalContent = [
          `테마명: ${themeName.trim()}`,
          `설명: ${themeDesc.trim()}`,
          `난이도: ${DIFFICULTY_META[themeDifficulty]?.label || themeDifficulty}`,
          `장르: ${themeGenre}`,
          `플레이타임: ${themePlaytime.trim()}분`,
          `휴게시간: ${themeBreakTime.trim()}분`,
          `인원별 가격: ${themePricing.trim() || '(미기재)'}`,
        ].join('\n');
        await createInquiry({
          uid: user.uid, targetType: 'app', storeId: effectiveStoreId, context,
          title: finalTitle, content: finalContent, senderRole, category, imageUrl,
        });
        showAlert('접수 완료', '문의가 접수됐어요. 답변이 등록되면 알려드릴게요.', [
          { text: '확인', onPress: () => navigation.goBack() },
        ]);
      } catch (e) {
        showAlert('오류', '문의 등록 중 오류가 발생했어요.\n' + e.message);
      } finally { setSaving(false); setUploadingImage(false); }
      return;
    }

    if (!title.trim() || !content.trim()) {
      showAlert('입력 필요', '제목과 내용을 모두 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      await createInquiry({ uid: user.uid, targetType, storeId: defaultStoreId, context, title, content, senderRole, category });
      showAlert('접수 완료', '문의가 접수됐어요. 답변이 등록되면 알려드릴게요.', [
        { text: '확인', onPress: () => navigation.goBack() },
      ]);
    } catch (e) {
      showAlert('오류', '문의 등록 중 오류가 발생했어요.\n' + e.message);
    } finally { setSaving(false); }
  };

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={s.container}>
        <AppHeader showBack centerElement={<Text style={{ color: TEXT, fontSize: 15, fontWeight: '800' }}>{targetType === 'store' ? '매장 문의하기' : '앱 문의하기'}</Text>} />

        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32 }}>
          {context?.type && (
            <View style={s.ctxBox}>
              {CONTEXT_LABEL[context.type]
                ? <IconLabel Icon={CONTEXT_LABEL[context.type].Icon} size={s.ctxLabel.fontSize || 13} color={s.ctxLabel.color} textStyle={s.ctxLabel}>{CONTEXT_LABEL[context.type].label}</IconLabel>
                : <Text style={s.ctxLabel}>문의 대상</Text>}
              {context.productName ? <Text style={s.ctxSub}>{context.productName}</Text> : null}
            </View>
          )}

          <Text style={s.label}>카테고리</Text>
          <View style={s.categoryRow}>
            {categories.map(c => (
              <TouchableOpacity
                key={c.value}
                style={[s.categoryChip, category === c.value && s.categoryChipOn]}
                onPress={() => setCategory(c.value)}>
                <Text style={[s.categoryChipTxt, category === c.value && s.categoryChipTxtOn]}>{c.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {isThemeRequest ? (
            <>
              {needsStorePicker && (
                <>
                  <Text style={s.label}>매장 선택</Text>
                  <View style={s.categoryRow}>
                    {storeOptions.map(o => (
                      <TouchableOpacity
                        key={o.id}
                        style={[s.categoryChip, selectedStoreId === o.id && s.categoryChipOn]}
                        onPress={() => setSelectedStoreId(o.id)}>
                        <Text style={[s.categoryChipTxt, selectedStoreId === o.id && s.categoryChipTxtOn]}>{o.ownerName}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </>
              )}

              <Text style={s.label}>테마명</Text>
              <TextInput
                style={s.input} value={themeName} onChangeText={setThemeName}
                placeholder="테마명을 입력해주세요" placeholderTextColor={TEXT_MUTED}
              />

              <Text style={s.label}>설명</Text>
              <TextInput
                style={[s.input, s.textareaSmall]} value={themeDesc} onChangeText={setThemeDesc}
                placeholder="테마 설명을 입력해주세요" placeholderTextColor={TEXT_MUTED} multiline
              />

              <Text style={s.label}>난이도</Text>
              <View style={s.categoryRow}>
                {DIFFICULTY_OPTIONS.map(d => (
                  <TouchableOpacity
                    key={d.value}
                    style={[s.categoryChip, themeDifficulty === d.value && s.categoryChipOn]}
                    onPress={() => setThemeDifficulty(d.value)}>
                    <Text style={[s.categoryChipTxt, themeDifficulty === d.value && s.categoryChipTxtOn]}>{d.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={s.label}>장르</Text>
              <View style={s.categoryRow}>
                {GENRES.map(g => (
                  <TouchableOpacity
                    key={g}
                    style={[s.categoryChip, themeGenre === g && s.categoryChipOn]}
                    onPress={() => setThemeGenre(g)}>
                    <Text style={[s.categoryChipTxt, themeGenre === g && s.categoryChipTxtOn]}>{g}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={s.label}>플레이타임(분)</Text>
              <TextInput
                style={s.input} value={themePlaytime} onChangeText={setThemePlaytime}
                placeholder="예: 60" placeholderTextColor={TEXT_MUTED} keyboardType="number-pad"
              />

              <Text style={s.label}>휴게시간(분)</Text>
              <TextInput
                style={s.input} value={themeBreakTime} onChangeText={setThemeBreakTime}
                placeholder="예: 10 (다음 예약과의 간격)" placeholderTextColor={TEXT_MUTED} keyboardType="number-pad"
              />

              <Text style={s.label}>인원별 가격 (선택)</Text>
              <TextInput
                style={s.input} value={themePricing} onChangeText={setThemePricing}
                placeholder="예: 2인 24,000원, 3인 33,000원" placeholderTextColor={TEXT_MUTED}
              />

              <Text style={s.label}>테마 이미지</Text>
              {themeImageUri ? (
                <View style={s.imagePreviewWrap}>
                  <Image source={{ uri: themeImageUri }} style={s.imagePreview} />
                  <TouchableOpacity
                    style={s.imageRemoveBtn}
                    accessibilityLabel="이미지 제거"
                    onPress={() => setThemeImageUri(null)}>
                    <Text style={{ color: TEXT, fontSize: 13, fontWeight: '800' }}>✕</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity style={s.imagePickBtn} onPress={handlePickThemeImage}>
                  <Text style={{ color: GOLD, fontSize: 13, fontWeight: '700' }}>+ 사진 선택</Text>
                </TouchableOpacity>
              )}
            </>
          ) : (
            <>
              <Text style={s.label}>제목</Text>
              <TextInput
                style={s.input}
                value={title}
                onChangeText={setTitle}
                placeholder="문의 제목을 입력해주세요"
                placeholderTextColor={TEXT_MUTED}
              />

              <Text style={s.label}>내용</Text>
              <TextInput
                style={[s.input, s.textarea]}
                value={content}
                onChangeText={setContent}
                placeholder="문의하실 내용을 자세히 적어주세요"
                placeholderTextColor={TEXT_MUTED}
                multiline
              />
            </>
          )}

          <GradientButton
            label={saving ? (uploadingImage ? '이미지 업로드 중...' : '접수 중...') : '문의 접수하기'}
            onPress={handleSubmit}
            disabled={saving}
            style={s.submitBtn}
          />
        </ScrollView>
      </View>
    </KeyboardAvoidingView>
  );
}

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: BG },

  ctxBox:      { backgroundColor: GOLD + '12', borderRadius: 10, borderWidth: 1, borderColor: GOLD + '35',
                 padding: 12, marginBottom: 16 },
  ctxLabel:    { color: GOLD, fontSize: 13, fontWeight: '800' },
  ctxSub:      { color: TEXT_MUTED, fontSize: 12, marginTop: 2 },

  label:       { color: TEXT_MUTED, fontSize: 12, marginBottom: 6, marginTop: 4 },
  categoryRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  categoryChip:   { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, borderWidth: 1,
                    borderColor: BORDER, backgroundColor: SURFACE2 },
  categoryChipOn: { borderColor: GOLD, backgroundColor: GOLD + '18' },
  categoryChipTxt:   { color: TEXT_MUTED, fontSize: 12, fontWeight: '600' },
  categoryChipTxtOn: { color: GOLD, fontWeight: '800' },
  input:       { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, borderRadius: 10,
                 paddingHorizontal: 14, height: 46, color: TEXT, fontSize: 14, marginBottom: 14 },
  textarea:    { height: 140, paddingVertical: 12, textAlignVertical: 'top' },
  textareaSmall: { height: 90, paddingVertical: 12, textAlignVertical: 'top' },
  submitBtn:   { marginTop: 8 },

  imagePickBtn: { height: 46, borderRadius: 10, borderWidth: 1, borderColor: GOLD + '50',
                  borderStyle: 'dashed', backgroundColor: SURFACE2, alignItems: 'center',
                  justifyContent: 'center', marginBottom: 14 },
  imagePreviewWrap: { marginBottom: 14, position: 'relative', width: 140, height: 105 },
  imagePreview: { width: 140, height: 105, borderRadius: 10 },
  imageRemoveBtn: { position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: 12,
                    backgroundColor: '#00000090', alignItems: 'center', justifyContent: 'center' },
});
